<div class="operations-shell">
  <main class="report-area">
    <div class="report-frame-wrap">
      <div class="loading-overlay" *ngIf="isLoading">
        <div class="spinner"></div>
        <p>Loading Power BI {{ activeDashboardType }} report...</p>
      </div>

      <!-- Restricted Access / Permission Denied Card -->
      <div class="permission-denied-container" *ngIf="hasPermissionError && !isLoading">
        <div class="permission-card">
          <div class="icon-wrap">
            <i class="fas fa-lock"></i>
          </div>
          <h3 class="permission-title">Access Restricted</h3>
          <p class="permission-text">{{ permissionMessage }}</p>
          <button class="btn btn-secondary retry-btn" (click)="loadReport()">
            <i class="fas fa-redo"></i> Retry
          </button>
        </div>
      </div>

      <div #reportContainer class="report-container" [class.hidden-report]="hasPermissionError"></div>
    </div>

    <footer class="footer-bar">
      <span>System Status: All Systems Normal</span>
      <span>© {{ currentYear }} Smart Poultry</span>
    </footer>
  </main>

  <!-- Refresh Schedule Modal -->
  <div class="modal-overlay" *ngIf="showRefreshModal">
    <div class="refresh-modal-box">
      <div class="modal-header">
        <h2><span class="refresh-icon">↻</span> Refresh</h2>
        <button class="close-btn" (click)="closeRefreshModal()">×</button>
      </div>
      
      <!-- VIEW MODE -->
      <div class="modal-body" *ngIf="!isEditingSchedule">
        <!-- Case A: No schedule set -->
        <div class="no-schedule-box" *ngIf="!hasScheduleSet()">
          <p class="no-schedule-text">There is no refresh schedule set for this report.</p>
          <button class="btn btn-primary create-btn" (click)="startCreateSchedule()">Create Refresh Schedule</button>
        </div>

        <!-- Case B: Schedule exists -->
        <div class="schedule-details-box" *ngIf="hasScheduleSet()">
          <div class="detail-row">
            <span class="detail-label">Status:</span>
            <span class="detail-value">
              <span class="status-badge" [class.enabled]="scheduleData.enabled">
                {{ scheduleData.enabled ? 'Enabled' : 'Disabled' }}
              </span>
            </span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Time zone:</span>
            <span class="detail-value">{{ getTimeZoneLabel(scheduleData.localTimeZoneId) }}</span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Frequency:</span>
            <span class="detail-value">{{ refreshFrequency }}</span>
          </div>

          <div class="detail-row" *ngIf="refreshFrequency === 'Weekly'">
            <span class="detail-label">Days:</span>
            <span class="detail-value">{{ scheduleData.days.join(', ') }}</span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Refresh Times:</span>
            <div class="detail-value times-grid">
              <span class="time-badge" *ngFor="let t of scheduleData.times">
                {{ formatTimeTo12Hour(t) }}
              </span>
            </div>
          </div>

          <div class="detail-row" *ngIf="notifyOthersEnabled && customEmails">
            <span class="detail-label">Failure Notifications:</span>
            <span class="detail-value">{{ customEmails }}</span>
          </div>
          <div class="detail-row" *ngIf="!notifyOthersEnabled || !customEmails">
            <span class="detail-label">Failure Notifications:</span>
            <span class="detail-value">No Notification</span>
          </div>

          <div class="view-actions">
            <button class="btn btn-primary" (click)="startEditSchedule()">Update Schedule</button>
          </div>
        </div>
      </div>

      <!-- EDIT MODE -->
      <div class="modal-body" *ngIf="isEditingSchedule">
        <!-- Time zone section -->
        <div class="form-group">
          <label>Time zone</label>
          <p class="help-text">
            <i class="info-icon">ℹ</i> Time zone configuration is applied not only to determine the schedule refresh time but also to establish the current date and time for incremental refresh models during on-demand and API refreshes. <a href="https://learn.microsoft.com/en-us/power-bi/connect-data/refresh-scheduled-refresh" target="_blank">Learn more</a>
          </p>
          <select class="form-control" [(ngModel)]="scheduleData.localTimeZoneId">
            <option value="Arab Standard Time">(UTC+03:00) Kuwait, Riyadh</option>
            <option value="UTC">(UTC) Coordinated Universal Time</option>
            <option value="Bangladesh Standard Time">(UTC+06:00) Dhaka</option>
          </select>
        </div>

        <!-- Configure a refresh schedule section -->
        <div class="form-group">
          <label>Configure a refresh schedule</label>
          <p class="help-text">
            Define a data refresh schedule to import data from the data source into the semantic model. <a href="https://learn.microsoft.com/en-us/power-bi/connect-data/refresh-scheduled-refresh" target="_blank">Learn more</a>
          </p>
          
          <div class="toggle-container">
            <label class="switch">
              <input type="checkbox" [(ngModel)]="scheduleData.enabled" />
              <span class="slider round"></span>
            </label>
            <span class="toggle-label">{{ scheduleData.enabled ? 'On' : 'Off' }}</span>
          </div>
        </div>

        <!-- Settings visible only when enabled -->
        <div class="enabled-settings" *ngIf="scheduleData.enabled">
          <!-- Frequency -->
          <div class="form-group">
            <label>Refresh frequency</label>
            <select class="form-control" [(ngModel)]="refreshFrequency" (change)="onFrequencyChange()">
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
            </select>
          </div>

          <!-- Days selection (Weekly only) -->
          <div class="form-group" *ngIf="refreshFrequency === 'Weekly'">
            <label>Days</label>
            <div class="days-checkboxes">
              <label *ngFor="let day of allDays">
                <input type="checkbox" [checked]="isDaySelected(day)" (change)="toggleDay(day)" />
                {{ day }}
              </label>
            </div>
          </div>

          <!-- Times list -->
          <div class="form-group">
            <label>Time</label>
            <div class="times-list">
              <div class="time-row" *ngFor="let slot of timeSlots; let i = index">
                <select class="time-select" [(ngModel)]="slot.hour">
                  <option *ngFor="let h of hoursList" [value]="h">{{ h }}</option>
                </select>
                <span class="time-separator">:</span>
                <select class="time-select" [(ngModel)]="slot.minute">
                  <option *ngFor="let m of minutesList" [value]="m">{{ m }}</option>
                </select>
                <select class="time-select" [(ngModel)]="slot.period">
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
                <button class="delete-time-btn" (click)="removeTime(i)">×</button>
              </div>
              
              <button class="add-time-link" *ngIf="timeSlots.length < 8" (click)="addTime()">
                + Add another time
              </button>
              <span class="limit-msg" *ngIf="timeSlots.length >= 8">
                Maximum 8 refresh times per day allowed.
              </span>
            </div>
          </div>
        </div>

        <!-- Notifications section -->
        <div class="form-group notification-section">
          <label>Send refresh failure and critical warning notifications to</label>
          
          <div class="checkbox-row">
            <input type="checkbox" id="notifyOthers" [(ngModel)]="notifyOthersEnabled" />
            <label for="notifyOthers">These contacts:</label>
          </div>

          <div class="others-emails" *ngIf="notifyOthersEnabled">
            <input type="text" class="form-control" placeholder="Enter email addresses separated by semicolons" [(ngModel)]="customEmails" />
            <span class="note-text">
              ℹ️ Note: Please ensure these emails are added to your Microsoft Fabric workspace; otherwise, notification emails will not be sent.
            </span>
          </div>
        </div>
      </div>

      <div class="modal-footer" *ngIf="isEditingSchedule">
        <button class="btn btn-primary" (click)="applyRefreshSchedule()">Apply</button>
        <button class="btn btn-secondary" (click)="discardEditSchedule()">Discard</button>
      </div>
      <div class="modal-footer" *ngIf="!isEditingSchedule">
        <button class="btn btn-secondary" (click)="closeRefreshModal()">Close</button>
      </div>
    </div>
  </div>

  <!-- Admin Power BI Settings Modal -->
  <div class="modal-overlay" *ngIf="showAdminSettingsModal">
    <div class="admin-settings-modal-box">
      <div class="modal-header">
        <h2><i class="fas fa-sliders-h"></i> Power BI Dataset Configuration</h2>
        <button class="close-btn" (click)="closeAdminSettingsModal()">×</button>
      </div>

      <div class="modal-body">
        <p class="settings-intro">
          Configure the Fabric Dataset ID for each user role in your tenant. If a Fabric Dataset ID is empty for a dashboard type, users with that role will not be able to access that dashboard.
        </p>

        <div class="loading-overlay" *ngIf="isLoadingRolesConfig">
          <div class="spinner"></div>
          <p>Loading role configurations...</p>
        </div>

        <div class="table-responsive" *ngIf="!isLoadingRolesConfig">
          <table class="config-table">
            <thead>
              <tr>
                <th>Role Name</th>
                <th>Operational Fabric Dataset ID</th>
                <th>Financial Fabric Dataset ID</th>
                <th>Executive Fabric Dataset ID</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of rolesConfigList">
                <td class="role-name-cell">
                  <span class="role-badge">{{ item.roleName }}</span>
                </td>
                <td>
                  <input type="text" class="form-control dataset-input" [(ngModel)]="item.operationalFabricId" placeholder="e.g. 85f022e6-c9e4-4324-b6a1-7c51c9aef9f3" />
                </td>
                <td>
                  <input type="text" class="form-control dataset-input" [(ngModel)]="item.financialFabricId" placeholder="Leave empty to restrict access" />
                </td>
                <td>
                  <input type="text" class="form-control dataset-input" [(ngModel)]="item.executiveFabricId" placeholder="Leave empty to restrict access" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-primary" (click)="saveAdminSettingsConfig()" [disabled]="isSavingRolesConfig || isLoadingRolesConfig">
          <span *ngIf="!isSavingRolesConfig"><i class="fas fa-save"></i> Save Settings</span>
          <span *ngIf="isSavingRolesConfig"><i class="fas fa-spinner fa-spin"></i> Saving...</span>
        </button>
        <button class="btn btn-secondary" (click)="closeAdminSettingsModal()" [disabled]="isSavingRolesConfig">Cancel</button>
      </div>
    </div>
  </div>
</div>
